import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calculator,
  ArrowRight,
  ShieldCheck,
  Wallet,
  Clock3,
  Percent,
} from "lucide-react";
import "../assets/css/loanCalculator.css";

const LOAN_SETTINGS_API = `${process.env.REACT_APP_API_BASE}/loans/loan_settings.php`;

type LoanProduct = {
  id: string;
  product_name: string;
  min_amount: string;
  max_amount: string;
  min_tenure: string;
  max_tenure: string;
  interest_rate: string;
  upfront_fee_rate: string;
  status: string;
};

const formatMoney = (value: number) =>
  `₦${Number(value || 0).toLocaleString("en-NG", {
    maximumFractionDigits: 0,
  })}`;

const onlyNumber = (value: string) => value.replace(/\D/g, "");

export default function LoanCalculator() {
  const [products, setProducts] = useState<LoanProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [amount, setAmount] = useState("");
  const [tenor, setTenor] = useState("");
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState("");

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setFetchError("");

        const res = await fetch(LOAN_SETTINGS_API, {
          method: "GET",
          credentials: "include",
        });

        const data = await res.json();

        if (!data.success) {
          setFetchError("Unable to load loan products.");
          return;
        }

        const activeProducts = (data.products || []).filter(
          (item: LoanProduct) => item.status === "active"
        );

        setProducts(activeProducts);

        if (activeProducts.length) {
          const first = activeProducts[0];

          setSelectedProduct(first.product_name);
          setAmount(String(Number(first.min_amount || 0)));
          setTenor(String(Number(first.min_tenure || 1)));
        }
      } catch (err) {
        console.error("Loan product fetch failed:", err);
        setFetchError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const product = products.find((p) => p.product_name === selectedProduct);

  const minAmount = Number(product?.min_amount || 0);
  const maxAmount = Number(product?.max_amount || 0);
  const minTenure = Number(product?.min_tenure || 1);
  const maxTenure = Number(product?.max_tenure || 24);
  const monthlyRate = Number(product?.interest_rate || 0);
  const upfrontFeeRate = Number(product?.upfront_fee_rate || 0);

  const loanAmount = Number(amount || 0);
  const loanTenor = Number(tenor || minTenure || 1);

  const amountError =
    product && loanAmount
      ? loanAmount < minAmount
        ? `Minimum amount is ${formatMoney(minAmount)}`
        : loanAmount > maxAmount
        ? `Maximum amount is ${formatMoney(maxAmount)}`
        : ""
      : "";

  const monthlyInterest = useMemo(() => {
    return (loanAmount * monthlyRate) / 100;
  }, [loanAmount, monthlyRate]);

  const totalInterest = useMemo(() => {
    return monthlyInterest * loanTenor;
  }, [monthlyInterest, loanTenor]);

  const upfrontFee = useMemo(() => {
    return (loanAmount * upfrontFeeRate) / 100;
  }, [loanAmount, upfrontFeeRate]);

  const totalRepayment = loanAmount + totalInterest;
  const monthlyRepayment = loanTenor > 0 ? totalRepayment / loanTenor : 0;
  const estimatedDisbursement = Math.max(loanAmount - upfrontFee, 0);

  const tenorOptions = useMemo(() => {
    if (!product) return [];

    return Array.from(
      { length: maxTenure - minTenure + 1 },
      (_, index) => minTenure + index
    );
  }, [product, minTenure, maxTenure]);

  const handleProductChange = (productName: string) => {
    const nextProduct = products.find((item) => item.product_name === productName);

    setSelectedProduct(productName);

    if (nextProduct) {
      setAmount(String(Number(nextProduct.min_amount || 0)));
      setTenor(String(Number(nextProduct.min_tenure || 1)));
    }
  };

  return (
    <main className="loan-calculator-page">
      <section className="loan-calculator-hero">
        <div>
          <span className="calculator-badge">
            <Calculator size={16} />
            Loan Calculator
          </span>

          <h1>Estimate your loan repayment before applying</h1>

          <p>
            Select a loan product, enter your preferred amount and tenor, and
            see your estimated monthly repayment instantly.
          </p>

          <div className="calculator-trust-row">
            <span>
              <ShieldCheck size={16} /> Transparent estimate
            </span>
            <span>
              <Clock3 size={16} /> Instant calculation
            </span>
            <span>
              <Wallet size={16} /> Apply when ready
            </span>
          </div>
        </div>
      </section>

      <section className="loan-calculator-wrapper">
        <div className="calculator-form-card">
          <h2>Loan Details</h2>

          {fetchError && <div className="calculator-error">{fetchError}</div>}

          <label>Loan Product</label>
          <select
            value={selectedProduct}
            onChange={(e) => handleProductChange(e.target.value)}
            disabled={loading || !products.length}
          >
            {loading && <option>Loading products...</option>}

            {!loading && !products.length && <option>No active product found</option>}

            {!loading &&
              products.map((item) => (
                <option key={item.id} value={item.product_name}>
                  {item.product_name}
                </option>
              ))}
          </select>

          {product && (
            <div className="product-range-box">
              <span>
                Amount: <strong>{formatMoney(minAmount)}</strong> -{" "}
                <strong>{formatMoney(maxAmount)}</strong>
              </span>
              <span>
                Tenure: <strong>{minTenure}</strong> -{" "}
                <strong>{maxTenure}</strong> months
              </span>
            </div>
          )}

          <label>Loan Amount</label>
          <input
            value={amount ? Number(amount).toLocaleString() : ""}
            onChange={(e) => setAmount(onlyNumber(e.target.value))}
            placeholder="Enter loan amount"
          />

          {amountError && <small className="calculator-field-error">{amountError}</small>}

          <label>Tenor in Months</label>
          <select value={tenor} onChange={(e) => setTenor(e.target.value)}>
            {tenorOptions.map((month) => (
              <option key={month} value={month}>
                {month} {month === 1 ? "Month" : "Months"}
              </option>
            ))}
          </select>

          <div className="rate-pill">
            <Percent size={16} />
            Monthly interest rate: <strong>{monthlyRate}%</strong>
          </div>
        </div>

        <div className="calculator-result-card">
          <span>Estimated Repayment</span>

          <h2>{formatMoney(monthlyRepayment)}</h2>
          <p>Monthly repayment</p>

          <div className="result-breakdown">
            <div>
              <small>Loan Amount</small>
              <strong>{formatMoney(loanAmount)}</strong>
            </div>

            <div>
              <small>Monthly Interest</small>
              <strong>{formatMoney(monthlyInterest)}</strong>
            </div>

            <div>
              <small>Total Interest</small>
              <strong>{formatMoney(totalInterest)}</strong>
            </div>

            <div>
              <small>Total Repayment</small>
              <strong>{formatMoney(totalRepayment)}</strong>
            </div>

            <div>
              <small>Upfront Fee ({upfrontFeeRate}%)</small>
              <strong>{formatMoney(upfrontFee)}</strong>
            </div>

            <div>
              <small>Estimated Disbursement</small>
              <strong>{formatMoney(estimatedDisbursement)}</strong>
            </div>
          </div>

          <div className="repayment-progress">
            <span />
          </div>

          <Link
            to="/applyloan"
            className={`calculator-apply-btn ${amountError ? "disabled" : ""}`}
          >
            Apply for Loan <ArrowRight size={17} />
          </Link>

          <small className="calculator-note">
            Final repayment may change after verification, approval, fees, and
            official loan offer.
          </small>
        </div>
      </section>
    </main>
  );
}